import { useMemo } from 'react';
import { SpacedDash, ModalWrapper, NewModalBodyWrapper, QueryBoundary, GuideLinkButton } from '@mews-commander/ui';
import { Badge, Button, Container, EmptyState, Icon, IconName, Stack, TableHeader } from '@optimus-web/core';
import { DateTimeService, ISOFormat, LocalizedDate, lt, useFeatureFlag, useToggle } from '@mews/framework';
import { eqUniversal } from '@mews/fp';
import { FeatureFlag } from '@mews-commander/enums';
import { ORDERED_LEDGER_TABLES, LedgersActivityReportRoute } from '../constants';
import { useDefaultLedgerActivityReportFilterValues } from '../schema';
import { useIsEffectiveDateBeforeCurrentDate } from '../hooks/useAccountingLedgersEffectiveDateQuery';
import { LedgerActivityReportHighlightCard } from './LedgerActivityReportHighlightCard';
import { LedgerActivityReportTable } from './LedgerActivityReportTable';
import { LedgerActivityReportFilters } from './LedgerActivityReportFilters';
import { LedgerActivityReportExport } from './LedgerActivityReportExport';

const LEDGER_ACTIVITY_REPORT_BETA_GUIDE_LINK =
    'https://mews.atlassian.net/wiki/external/ODIzZjg0YjFiNjNhNDI4ZTgwZGQ3OTc3ODgyN2Q1NmQ';

const LedgerActivityHeaderDate = ({ startDate, endDate }: { startDate: string; endDate: string }) => {
    const parsedStartDate = DateTimeService.fromString(startDate, ISOFormat.Date);
    const parsedEndDate = DateTimeService.fromString(endDate, ISOFormat.Date);

    const areDatesEqual = DateTimeService.isEqual(parsedStartDate, parsedEndDate);

    return (
        <>
            <LocalizedDate date={parsedStartDate} />
            {areDatesEqual ? null : (
                <>
                    {SpacedDash}
                    <LocalizedDate date={parsedEndDate} />
                </>
            )}
        </>
    );
};

export const LedgerActivityReport = () => {
    const ledgerActivityReportExportEnabled = useFeatureFlag(FeatureFlag.ledgerActivityReportExportEnabled);
    const isEffectiveDateBeforeNow = useIsEffectiveDateBeforeCurrentDate();
    const defaultLedgerActivityReportFilterValues = useDefaultLedgerActivityReportFilterValues();
    const initialUrlValues = LedgersActivityReportRoute.useSearch();

    const filterValues = useMemo(
        () => ({
            ...defaultLedgerActivityReportFilterValues,
            ...initialUrlValues,
        }),
        [defaultLedgerActivityReportFilterValues, initialUrlValues]
    );
    const [open, toggleSidebar] = useToggle({ initialValue: false });
    const activeFilters = useMemo(() => {
        const { sort: searchSort, ...filterValuesWithoutSort } = filterValues;
        const { sort: defaultSort, ...defaultValuesWithoutSort } = defaultLedgerActivityReportFilterValues;
        return !eqUniversal.equals(filterValuesWithoutSort, defaultValuesWithoutSort);
    }, [filterValues, defaultLedgerActivityReportFilterValues]);

    if (!isEffectiveDateBeforeNow) {
        return <EmptyState pictogram="error" primaryText={lt('NoReportAvailable')} />;
    }

    return (
        <ModalWrapper>
            <NewModalBodyWrapper>
                <Stack fill>
                    <Container padding={['200', '400']} overflowY="auto" width="100%">
                        <TableHeader
                            title={lt('LedgerActivityReport')}
                            subTitle={
                                <LedgerActivityHeaderDate
                                    startDate={filterValues.startDate}
                                    endDate={filterValues.endDate}
                                />
                            }
                            rightSide={
                                <Stack spacing="100" itemAlignment="center">
                                    <Button onClick={toggleSidebar} size="small" type="button">
                                        <Badge active={activeFilters} outline data-test-id="filters-applied-badge">
                                            <Icon value={IconName.Filter} size="small" />
                                        </Badge>
                                        {lt('Filter')}
                                    </Button>
                                    {ledgerActivityReportExportEnabled && (
                                        <LedgerActivityReportExport filterValues={filterValues} />
                                    )}
                                    <GuideLinkButton size="small" guideLink={LEDGER_ACTIVITY_REPORT_BETA_GUIDE_LINK} />
                                </Stack>
                            }
                        />
                        <Container data-test-id="highlights-ledger-activity">
                            <Stack
                                spacing="200"
                                distribution="stretch"
                                tabletMax={{
                                    vertical: true,
                                }}
                            >
                                {ORDERED_LEDGER_TABLES.map(({ ledgerType, title, showTotalCard, totalCardTitle }) =>
                                    showTotalCard ? (
                                        <QueryBoundary loader="inline" key={`${ledgerType}-card`}>
                                            <LedgerActivityReportHighlightCard
                                                ledgerType={ledgerType}
                                                title={title}
                                                totalCardTitle={totalCardTitle}
                                                filterValues={filterValues}
                                            />
                                        </QueryBoundary>
                                    ) : null
                                )}
                            </Stack>
                        </Container>
                        <Stack vertical>
                            {ORDERED_LEDGER_TABLES.map(({ ledgerType, title }) => (
                                <QueryBoundary loader="inline" key={`${ledgerType}-table`}>
                                    <LedgerActivityReportTable
                                        ledgerType={ledgerType}
                                        title={title}
                                        filterValues={filterValues}
                                        key={`${ledgerType}-table`}
                                    />
                                </QueryBoundary>
                            ))}
                        </Stack>
                    </Container>
                    <LedgerActivityReportFilters
                        open={open}
                        toggleSidebar={toggleSidebar}
                        filterValues={filterValues}
                    />
                </Stack>
            </NewModalBodyWrapper>
        </ModalWrapper>
    );
};
